import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { Wallpaper } from '../../components/Wallpaper';
import { ErrorPage } from '../ErrorPage';
import { isMobile } from '../../utils/utils';
import { DesktopFaceRecognition } from '../DesktopFaceRecognition';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useCookies } from 'react-cookie';
import { getOrCreatebrowserKey } from '../../utils/fingerprint_utils'

export const FaceRecognition = (props) => {
    const { clientThemeSettings } = useThemeContext();
    const {
        signData,
        setSignerStep,
        setSignData,
        signerHash
    } = props;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState({ has_error: false, status: 0 });
    const [isSdkReady, setIsSdkReady] = useState(false);
    const [cookies, setCookie] = useCookies([`qisign_${signerHash}`]);

    const sdkContainerRef = useRef(null);
    const instanceRef = useRef(null);

    const handleReturnDocumentDisplay = useCallback(() => {
        setSignerStep('document_display');
    }, [setSignerStep]);

    const getClientSessionKey = useCallback(async () => {
        if (!signData.signer_token) {
            setError({ has_error: true, status: 401 });
            return null;
        }
        try {
            const config = { headers: { Authorization: signData.signer_token } };
            const response = await axios.get('signer/liveness_client_session_key', config);
            const clientSessionKey = response.data.client_session_key;

            if (!clientSessionKey) {
                throw new Error("No 'client_session_key' found.");
            }
    
            return clientSessionKey;

        } catch (err) {
            const status = (err.response || {}).status;
            setError({ has_error: true, status: [401, 403].includes(status) ? status : 500 });
            return null;
        }
    }, [signData.signer_token]);

    const handleSuccess = useCallback(async (result) => {
        const imageKey = result.data;
        const browserKey = await getOrCreatebrowserKey();
        const payload = { event_date: new Date().toISOString(), image_key: imageKey, step: 'face_recognition', device_data: { device_key: browserKey } };
        const config = { headers: { Authorization: signData.signer_token } };

        axios.patch('/signer', payload, config)
            .then(response => {
                setSignData({ ...signData, ...response.data });
                setSignerStep(response.data.next_step);
            })
            .catch(err => {
                const status = (err.response || {}).status;
                setError({ has_error: true, status: [401, 403].includes(status) ? status : 500 });
            });
    }, [signData, setSignData, setSignerStep]);
    
    const handleError = useCallback((err) => {
        if (err?.reason === 'INVALID_TOKEN') {
            getClientSessionKey().then(newToken => {
                if (newToken && instanceRef.current) {
                    instanceRef.current.open(newToken)
                        .then(handleSuccess)
                        .catch(handleError);
                } else {
                    setError({ has_error: true, status: 401 });
                }
            });
        } else if (err?.reason === 'USER_CANCELED') {
            handleReturnDocumentDisplay();
        } else if (err?.reason === 'USER_BLOCKED') {
            setCookie(`qisign_${signerHash}`, cookies, { path: `/s/${signerHash}`, maxAge: 0 });
            setSignerStep("login");
        } else {
            setError({ has_error: true, status: 500 });
        }
    }, [getClientSessionKey, handleSuccess, handleReturnDocumentDisplay, setCookie, signerHash, cookies, setSignerStep]);

    useEffect(() => {
        const hostComponent = sdkContainerRef.current;
        if (!hostComponent) {
            return;
        }
        
        const webFaceRecon = new window.ZaigWebFaceRecon.WebFaceRecon(hostComponent)
            .setLogLevel('debug')
            .setSessionId(signData.session_id)
            .setThemeConfiguration({
                buttonColor: clientThemeSettings['button_color'],
                fontColor: clientThemeSettings['font_color'],
                backgroundColor: clientThemeSettings['background_color'],
            })
            .setShowBlockedScreen(true)
            .setShowInvalidTokenScreen(true)
            .setShowBackButton(signData.signature_settings?.document_display_before_face_recognition !== false);
        
        if (process.env.REACT_APP_ENV !== 'production') {
            webFaceRecon.setSandboxEnvironment();
        }
        
        instanceRef.current = webFaceRecon.build();
        instanceRef.current.initialize()
            .then(() => {
                setIsSdkReady(true);
            })
            .catch(initErr => {
                setError({ has_error: true, status: 500 });
            })
            .finally(() =>{
                setLoading(false); 
            });
        
        return () => {
            if (hostComponent) {
                hostComponent.innerHTML = '';
            }
        };
    }, [clientThemeSettings, signData.session_id, signData.signature_settings.document_display_before_face_recognition]);

    useEffect(() => {
        if (!isSdkReady || !instanceRef.current) {
            return;
        }

        const executeSdkOpen = async () => {
            const token = await getClientSessionKey();
            if (token) {
                instanceRef.current.open(token)
                    .then(handleSuccess)
                    .catch(handleError);
            }
        };
        executeSdkOpen();
    }, [isSdkReady, getClientSessionKey, handleSuccess, handleError]);

    if (!isMobile()) {
        return (
            <DesktopFaceRecognition {...props} clientThemeSettings={clientThemeSettings} loading={loading} />
        );
    }

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1000
        }}>
            <Wallpaper
                id="faceReconContainer"
                backgroundColor={clientThemeSettings['background_color']}
                style={{ width: '100%', height: '100%', padding: 0 }}
            >
                <div ref={sdkContainerRef} style={{ width: '100%', height: '100%' }} />

                {error.has_error && (
                    <ErrorPage
                        error={error}
                        redirectToLogin={() => setSignerStep('login')}
                        resetError={() => setError({ has_error: false, status: 0 })}
                        clientThemeSettings={clientThemeSettings}
                    />
                )}
            </Wallpaper>
        </div>
    );  
};